import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { getAdminData, postInstituteAppData, deleteInstituteAppData, apiPaths } from 'modules/platform-components/api-integration-util';
import { getUserDetails } from 'modules/platform-components/utils/shared-data.service';
import CloseIcon from '@mui/icons-material/Close';
import { checkPermission } from 'modules/platform-components/utils/configuration';

import permissionCategories from './pages.json';


const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRoleId, setCurrentRoleId] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const userDetails = getUserDetails();

      // Fetch roles
      const rolesResponse = await getAdminData(apiPaths.acadRoles, {
        headers: {
          tenantId: userDetails.tenantId,
          userEmail: userDetails.email
        }
      });
      if (rolesResponse.result && Array.isArray(rolesResponse.result)) {
        const formattedRoles = rolesResponse.result.map(role => ({
          ...role,
          permissions: role.rolePermissions ? JSON.parse(role.rolePermissions) : {}
        }));
        setRoles(formattedRoles);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenAddModal = () => {
    setEditMode(false);
    setCurrentRoleId(null);
    setRoleName('');
    setSelectedPermissions({});
    setOpenModal(true);
  };

  const handleOpenEditModal = (role) => {
    setEditMode(true);
    setCurrentRoleId(role.roleId);
    setRoleName(role.roleName);
    setSelectedPermissions(JSON.parse(JSON.stringify(role.permissions)));
    setOpenModal(true);
  };

  const handleCheckboxChange = (category, subCategory = null) => {
    setSelectedPermissions((prev) => {
      const updated = { ...prev };

      if (subCategory) {
        if (!updated[category]) updated[category] = [];

        if (updated[category].includes(subCategory)) {
          updated[category] = updated[category].filter(p => p !== subCategory);
        } else {
          updated[category].push(subCategory);
        }
      } else {
        // Toggle entire category
        if (updated[category]) {
          delete updated[category];
        } else {
          updated[category] = [...permissionCategories[category]];
        }
      }

      return updated;
    });
  };

  const handleSaveRole = async () => {
    if (!roleName.trim()) {
      showSnackbar('Role name is required', 'error');
      return;
    }

    try {
      const userDetails = getUserDetails();
      const roleData = {
        roleId: editMode ? currentRoleId : 0,
        roleName: roleName.trim(),
        appCode: 'ACAD_PORTAL',
        rolePermissions: JSON.stringify(selectedPermissions)
      };

      let response;
      if (editMode) {
        response = await postInstituteAppData(apiPaths.acadSaveRole, {
          data: { ...roleData, roleId: currentRoleId },
          headers: {
            tenantId: userDetails.tenantId,
            userEmail: userDetails.email
          }
        });

        // Update the existing role in state
        setRoles(prevRoles => prevRoles.map(role =>
          role.roleId === currentRoleId ? {
            ...role,
            roleName: roleName.trim(),
            permissions: selectedPermissions
          } : role
        ));
      } else {
        response = await postInstituteAppData(apiPaths.acadSaveRole, {
          data: roleData,
          headers: {
            tenantId: userDetails.tenantId,
            userEmail: userDetails.email
          }
        });

        // Add new role to state
        if (response.result) {
          fetchRoles();
        }
      }

      setOpenModal(false);
      showSnackbar(`Role ${editMode ? 'updated' : 'created'} successfully`);
    } catch (error) {
      console.error('Error saving role:', error);
      showSnackbar(`Failed to ${editMode ? 'update' : 'create'} role: ${error.message}`, 'error');
    }
  };

  const deleteRole = async (roleId) => {
    try {
      const userDetails = getUserDetails();

      const response = await deleteInstituteAppData(apiPaths.deleteRole, {
        body: [roleId],
        headers: {
          tenantId: userDetails.tenantId
        },
        params: {
          userEmail: userDetails.email
        }
      });

      if (response && (response.responseCode === 200 || response.responseCode === 0)) {
        showSnackbar(response.msg || 'Role deleted successfully');
        fetchRoles();
        return true;
      }

      throw new Error(response?.msg || 'Failed to delete role');
    } catch (error) {
      console.error('Error deleting role:', error);
      showSnackbar(error.message || 'Failed to delete role', 'error');
      return false;
    }
  };


  const isCategoryChecked = (categoryName) => {
    return !!selectedPermissions[categoryName];
  };

  const isSubCategoryChecked = (categoryName, subCategoryName) => {
    return selectedPermissions[categoryName]?.includes(subCategoryName) || false;
  };

  const formatPermissionsForDisplay = (permissions) => {
    return Object.entries(permissions).map(([category, subItems]) => ({
      category,
      subItems
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Role Management
        </Typography>

        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={4}>
          {checkPermission('Roles', 'Create New Role') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddModal}
              sx={{
                backgroundColor: '#3366FF',
                '&:hover': { backgroundColor: '#2952CC' },
                fontWeight: 'bold',
                px: 3,
                py: 1,
                textTransform: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(51, 102, 255, 0.2)'
              }}
            >
              Create New Role
            </Button>
          )}
        </Box>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 3
        }}>
          {roles.map(role => {
            const formattedPermissions = formatPermissionsForDisplay(role.permissions);

            return (
              <Card
                key={role.roleId}
                sx={{
                  p: 0,
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                  },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{
                  p: 2.5,
                  background: 'linear-gradient(135deg, #3366FF 0%, #5c85FF 100%)',
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{role.roleName}</Typography>
                  <Box>
                    {checkPermission('Roles', 'Edit Role') && (
                      <IconButton
                        size="small"
                        sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                        onClick={() => handleOpenEditModal(role)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {checkPermission('Roles', 'Delete Role') && (
                      <IconButton
                        size="small"
                        sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                        onClick={() => deleteRole(role.roleId)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <Box sx={{ p: 2.5, flexGrow: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#666', fontWeight: 600 }}>
                    Permissions ({formattedPermissions.length})
                  </Typography>

                  {formattedPermissions.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {formattedPermissions.map((item, index) => (
                        <Box key={index}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: '#3366FF',
                              fontWeight: 'bold',
                              mb: 0.5,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {item.category}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                            {item.subItems.length > 0 ? (
                              item.subItems.map((subItem, subIndex) => (
                                <Chip
                                  key={subIndex}
                                  label={subItem}
                                  size="small"
                                  sx={{
                                    height: '20px',
                                    fontSize: '0.7rem',
                                    backgroundColor: '#f0f0ff',
                                    color: '#3366FF',
                                    border: '1px solid #d0d0ff'
                                  }}
                                />
                              ))
                            ) : (
                              <Chip
                                label="Full Access"
                                size="small"
                                sx={{
                                  height: '20px',
                                  fontSize: '0.7rem',
                                  backgroundColor: '#f0fdf4',
                                  color: '#22c55e',
                                  border: '1px solid #c8e6c9'
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No permissions assigned
                    </Typography>
                  )}
                </Box>
              </Card>
            );
          })}
        </Box>
      </Box>

      {/* Role Modal */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#3366FF',
          color: 'white',
          py: 1.5
        }}>
          {editMode ? 'Edit Role' : 'Create New Role'}
          <IconButton onClick={() => setOpenModal(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          <Box sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="Role Name"
              variant="outlined"
              size="small"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 0.5, color: '#3366FF', fontWeight: 'bold' }}>Permissions</Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: '#64748b' }}>
              <strong>PAGE</strong> — what the user can see. <strong>ACTIONS</strong> — what buttons they can click. <strong>SCOPE</strong> — data access controls (e.g. All Subjects bypass).
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                size="small"
                variant="text"
                sx={{ fontSize: '0.7rem' }}
                onClick={() => {
                  const allPermissions = {};
                  Object.keys(permissionCategories).forEach(category => {
                    allPermissions[category] = [...permissionCategories[category]];
                  });
                  setSelectedPermissions(allPermissions);
                }}
              >
                SELECT ALL
              </Button>
              <Button
                size="small"
                variant="text"
                sx={{ fontSize: '0.7rem' }}
                onClick={() => setSelectedPermissions({})}
              >
                SELECT NONE
              </Button>
            </Box>

            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {Object.entries(permissionCategories).map(([category, subCategories]) => {
                const isActionsCategory = category.endsWith(' Actions');
                const isScopeCategory = category === 'Data Scope';
                const badge = isActionsCategory
                  ? { label: 'ACTIONS', bg: '#fef3c7', fg: '#92400e' }
                  : isScopeCategory
                    ? { label: 'SCOPE', bg: '#ede9fe', fg: '#5b21b6' }
                    : { label: 'PAGE', bg: '#e0e7ff', fg: '#3730a3' };
                return (
                  <Box key={category} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={isCategoryChecked(category)}
                            indeterminate={
                              selectedPermissions[category] &&
                              selectedPermissions[category].length > 0 &&
                              selectedPermissions[category].length < subCategories.length
                            }
                            onChange={() => handleCheckboxChange(category)}
                          />
                        }
                        label={<Typography variant="body2" fontWeight="bold">{category}</Typography>}
                        sx={{ mb: 0 }}
                      />
                      <Chip
                        label={badge.label}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 'bold',
                          backgroundColor: badge.bg,
                          color: badge.fg,
                        }}
                      />
                    </Box>

                    <Box sx={{ ml: 3, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {subCategories.map(subCategory => (
                        <FormControlLabel
                          key={subCategory}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedPermissions[category]?.includes(subCategory) || false}
                              onChange={() => handleCheckboxChange(category, subCategory)}
                            />
                          }
                          label={<Typography variant="caption">{subCategory}</Typography>}
                        />
                      ))}
                    </Box>
                    <Divider sx={{ mt: 1 }} />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button
            onClick={handleSaveRole}
            variant="contained"
            disabled={!roleName.trim()}
            sx={{ minWidth: 120 }}
          >
            {editMode ? 'Update Role' : 'Save Role'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Roles;